/**
 * Two-step modal for removing companies from watchlist.
 * System admins also see option to permanently delete from system.
 *
 * Step 1: Warning explaining consequences
 * Step 2: Confirmation checkbox + button
 * Step 3 (System Admin): Type company name to confirm permanent deletion
 */

import { useState, useCallback } from 'react';
import { AlertTriangle, Bookmark, Trash2, Loader2 } from 'lucide-react';
import { useUserRole } from '../../hooks/useUserRole';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import type { WatchlistEntry } from '../../types';

interface RemoveCompanyModalProps {
  isOpen: boolean;
  entry: WatchlistEntry | null;
  onClose: () => void;
  onConfirm: (companyId: string) => Promise<void>;
  /** System admin only: permanently delete company from system */
  onSystemDelete?: (companyId: string) => Promise<void>;
}

type ModalStep = 'warning' | 'confirm' | 'system-delete';

export function RemoveCompanyModal({
  isOpen,
  entry,
  onClose,
  onConfirm,
  onSystemDelete,
}: RemoveCompanyModalProps) {
  const [step, setStep] = useState<ModalStep>('warning');
  const [confirmed, setConfirmed] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // System delete confirmation
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const { isSystemAdmin } = useUserRole();

  // Reset state when modal closes
  const handleClose = useCallback(() => {
    setStep('warning');
    setConfirmed(false);
    setError(null);
    setIsRemoving(false);
    setDeleteConfirmText('');
    setIsDeleting(false);
    onClose();
  }, [onClose]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        handleClose();
      }
    },
    [handleClose]
  );

  const handleProceed = useCallback(() => {
    setStep('confirm');
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!entry || !confirmed) return;

    setIsRemoving(true);
    setError(null);

    try {
      await onConfirm(entry.company_id);
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove company');
      setIsRemoving(false);
    }
  }, [entry, confirmed, onConfirm, handleClose]);

  const handleSystemDeleteClick = useCallback(() => {
    setStep('system-delete');
    setDeleteConfirmText('');
    setError(null);
  }, []);

  const handleSystemDeleteConfirm = useCallback(async () => {
    if (!entry || !onSystemDelete) return;
    if (deleteConfirmText !== entry.company_name) return;

    setIsDeleting(true);
    setError(null);

    try {
      await onSystemDelete(entry.company_id);
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete company from system');
      setIsDeleting(false);
    }
  }, [entry, deleteConfirmText, onSystemDelete, handleClose]);

  if (!entry) return null;

  const isPinned = entry.source === 'group_pinned';
  const canSystemDelete = isSystemAdmin && onSystemDelete;
  const deleteTextMatches = deleteConfirmText === entry.company_name;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-md"
        data-testid="remove-company-modal"
        overlayTestId="remove-modal-backdrop"
        closeTestId="remove-modal-close"
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div
              className={`p-2 rounded-full ${step === 'system-delete' ? 'bg-red-200' : 'bg-red-100'}`}
            >
              <AlertTriangle
                className={`w-5 h-5 ${step === 'system-delete' ? 'text-red-700' : 'text-red-600'}`}
              />
            </div>
            <DialogTitle>
              {step === 'system-delete' ? 'Permanent Deletion' : `Remove ${entry.company_name}?`}
            </DialogTitle>
          </div>
          <DialogDescription className="sr-only">
            {step === 'system-delete'
              ? 'Permanently delete this company from the system'
              : 'Remove this company from your watchlist'}
          </DialogDescription>
        </DialogHeader>

        {/* Step 1: Warning */}
        {step === 'warning' && (
          <div data-testid="remove-step-warning">
            <div className="mb-4 text-sm text-gray-600">
              <p className="mb-3">
                Removing <strong>{entry.company_name}</strong> from your watchlist will:
              </p>
              <ul className="list-disc pl-5 space-y-1.5 text-gray-500">
                <li>Stop all pushed updates and reports for this company</li>
                <li>Remove the company from your navigation</li>
                <li>Keep your chat history and past reports accessible</li>
              </ul>
            </div>

            {isPinned && entry.group_name && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
                <div className="flex items-start gap-2">
                  <Bookmark className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="font-medium text-amber-800">Pinned by {entry.group_name}</p>
                    <p className="text-amber-700 mt-0.5">
                      This company was pinned by your group admin. Removing it will unpin it for all
                      group members.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
              <Button variant="outline" onClick={handleClose} data-testid="remove-cancel">
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleProceed} data-testid="remove-proceed">
                Continue
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Confirm */}
        {step === 'confirm' && (
          <div data-testid="remove-step-confirm">
            <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-md">
              <p className="text-sm text-gray-600">
                To confirm removal, please check the box below and click "Remove Company".
              </p>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <span className="text-sm text-red-700">{error}</span>
              </div>
            )}

            <label className="flex items-start gap-3 cursor-pointer mb-4">
              <Checkbox
                id="remove-confirm-checkbox"
                checked={confirmed}
                onCheckedChange={(checked) => setConfirmed(checked === true)}
                className="mt-0.5 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600"
                data-testid="remove-confirm-checkbox"
              />
              <span className="text-sm text-gray-700">
                I understand that I will stop receiving updates for{' '}
                <strong>{entry.company_name}</strong> and want to remove it from my watchlist.
              </span>
            </label>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
              <Button
                variant="outline"
                onClick={() => setStep('warning')}
                data-testid="remove-back"
              >
                Back
              </Button>
              <Button
                variant="destructive"
                onClick={handleConfirm}
                disabled={!confirmed || isRemoving}
                data-testid="remove-confirm-button"
              >
                {isRemoving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Removing...
                  </>
                ) : (
                  'Remove Company'
                )}
              </Button>
            </div>

            {/* System Admin: Delete from System option */}
            {canSystemDelete && (
              <div className="mt-6 pt-4 border-t-2 border-red-200">
                <div className="flex items-center gap-2 mb-3">
                  <div className="h-px flex-1 bg-red-200" />
                  <span className="text-xs font-semibold text-red-700 uppercase tracking-wide">
                    System Admin
                  </span>
                  <div className="h-px flex-1 bg-red-200" />
                </div>

                <div className="p-3 bg-red-50 border border-red-200 rounded-md mb-3">
                  <div className="flex items-start gap-2">
                    <Trash2 className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm">
                      <p className="font-medium text-red-800">Permanent System Deletion</p>
                      <p className="text-red-700 mt-0.5">
                        This will permanently delete <strong>{entry.company_name}</strong> and ALL
                        knowledge base data from the system. This cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>

                <Button
                  variant="outline"
                  onClick={handleSystemDeleteClick}
                  className="w-full border-2 border-red-300 text-red-700 hover:bg-red-50 hover:border-red-400"
                  data-testid="system-delete-button"
                >
                  Delete from System...
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Step 3: System Delete Confirmation (System Admin only) */}
        {step === 'system-delete' && canSystemDelete && (
          <div data-testid="remove-step-system-delete">
            <div className="mb-4 p-4 bg-red-100 border border-red-300 rounded-md">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-red-700 flex-shrink-0" />
                <div className="text-sm text-red-800">
                  <p className="font-bold mb-2">This will PERMANENTLY DELETE:</p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>
                      Company: <strong>{entry.company_name}</strong>
                    </li>
                    <li>All knowledge base documents</li>
                    <li>All events and news</li>
                    <li>All stored products and executives</li>
                  </ul>
                  <p className="mt-3 font-semibold">This action CANNOT be undone.</p>
                </div>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type "<span className="font-semibold text-red-700">{entry.company_name}</span>" to
                confirm:
              </label>
              <Input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder={entry.company_name}
                className="focus:ring-red-500 focus:border-red-500"
                data-testid="system-delete-confirm-input"
              />
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <span className="text-sm text-red-700">{error}</span>
              </div>
            )}

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
              <Button
                variant="outline"
                onClick={() => {
                  setStep('confirm');
                  setDeleteConfirmText('');
                  setError(null);
                }}
                data-testid="system-delete-back"
              >
                Back
              </Button>
              <Button
                onClick={handleSystemDeleteConfirm}
                disabled={!deleteTextMatches || isDeleting}
                className="bg-red-700 hover:bg-red-800 text-white"
                data-testid="system-delete-confirm-button"
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'DELETE PERMANENTLY'
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default RemoveCompanyModal;
